import React, { useEffect, useMemo, useState } from 'react';
import {
  Play,
  Pause,
  SkipForward,
  SkipBack,
  Heart,
  Users,
  BookOpen,
  HelpCircle,
  PhoneCall,
  ChevronLeft,
  Maximize2,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { useAudio } from '../context/AudioContext';
import { StationArtwork } from './StationArtwork';
import { YourDay } from './YourDay';
import { getPrayerTimes, getNextPrayer } from '../utils/prayerTimes';
import { fetchMajlisState } from '../api/client';
import { SERVICE_KEYS, isServiceVisible } from '../utils/services';

// صنفٌ حرفيّ لكل عدد: Tailwind لا يرى الأصناف المركّبة وقت البناء
const GRID_COLS = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' };

const MAJLIS_ROOM_ID = 'main';
// الحال يتغيّر بفتح المذيع أو إغلاقه، وهو حدث نادر — فالسؤال كل دقيقة يكفي
const MAJLIS_POLL_MS = 60000;

const MARKS = [
  { key: 'fajr', label: 'الفجر' },
  { key: 'dhuhr', label: 'الظهر' },
  { key: 'asr', label: 'العصر' },
  { key: 'maghrib', label: 'المغرب' },
  { key: 'isha', label: 'العشاء' },
];

/**
 * خط اليوم.
 *
 * المواقيت تسلسل زمني، فتُعرض كخط لا كشبكة مربّعات متطابقة: موضع العلامة
 * يقول أين أنت من النهار قبل أن تقرأ أي رقم.
 */
function DayLine({ onOpen }) {
  // الموقع من السياق: قراءته من localStorage هنا كانت تتجاهل الـ GPS،
  // فتعرض هذه الشاشة مكة بينما شاشة المواقيت تعرض موقعك الحقيقي.
  const { location } = useAudio();

  // العدّاد بالثواني: كان «الآن» يُحسب مرة عند الفتح فيتجمّد العدّاد
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const opts = useMemo(
    () => ({ lat: location.lat, lng: location.lng, timeZone: location.tz, methodId: location.method }),
    [location.lat, location.lng, location.tz, location.method]
  );
  // مواقيت اليوم تتغيّر بتغيّر اليوم لا كل ثانية
  const day = new Date(now).toLocaleDateString('en-CA', location.tz ? { timeZone: location.tz } : {});
  const times = useMemo(() => getPrayerTimes(opts), [opts, day]); // eslint-disable-line react-hooks/exhaustive-deps
  const next = getNextPrayer({ ...opts, now: new Date(now) });

  const start = times.times.fajr.getTime();
  const end = times.times.isha.getTime();
  const progress = Math.min(1, Math.max(0, (now - start) / (end - start)));

  return (
    <button
      onClick={onOpen}
      className="w-full text-right px-4 pt-5 pb-4 group"
      aria-label={`مواقيت ${location.name}. الصلاة القادمة ${next.name} بعد ${next.countdown}`}
    >
      <div className="flex items-baseline justify-between mb-4">
        <div className="flex items-baseline gap-2">
          <span className="text-sm text-ink-muted">{next.name} بعد</span>
          <span className="text-xl font-semibold text-primary" dir="ltr">
            {next.countdown}
          </span>
        </div>
        <span className="text-xs text-ink-muted flex items-center gap-0.5 group-hover:text-primary transition-colors">
          {location.name}
          <ChevronLeft className="w-3.5 h-3.5" aria-hidden="true" />
        </span>
      </div>

      {/*
        اليوم يجري من اليمين إلى اليسار كالقراءة: الفجر أوله فهو أيمنه،
        والعشاء آخره فهو أيسره. كان الشريط مضبوطاً على ltr فبدأ بالفجر
        من اليسار، عكس ما يتوقعه القارئ العربي.
      */}
      <div className="relative h-px bg-line mx-1" dir="rtl">
        <div
          className="absolute inset-y-0 right-0 bg-primary/30"
          style={{ width: `${progress * 100}%` }}
        />
        {MARKS.map((m, i) => (
          <span
            key={m.key}
            className="absolute translate-x-1/2 -top-1"
            style={{ right: `${(i / (MARKS.length - 1)) * 100}%` }}
          >
            <span
              className={`block rounded-full transition-all ${
                m.key === next.key
                  ? 'w-2.5 h-2.5 bg-primary -mt-[3px] ring-4 ring-primary/15'
                  : 'w-1.5 h-1.5 bg-line'
              }`}
            />
          </span>
        ))}
        <span
          className="absolute -top-2 translate-x-1/2 w-px h-5 bg-secondary"
          style={{ right: `${progress * 100}%` }}
          aria-hidden="true"
        />
      </div>

      <div className="flex justify-between mt-3 px-0.5" dir="rtl">
        {MARKS.map((m) => (
          <span key={m.key} className="flex flex-col items-center gap-0.5">
            <span className="text-2xs text-ink-muted">{m.label}</span>
            <span
              className={`text-xs tabular-nums ${
                m.key === next.key ? 'text-primary font-semibold' : 'text-ink/70'
              }`}
            >
              {times.formatted[m.key]}
            </span>
          </span>
        ))}
      </div>
    </button>
  );
}

/** مدخل هادئ — خانة من أربع في صفّ واحد، لتظهر المداخل كلها دون تمرير. */
function Entry({ icon: Icon, label, hint, onClick, accent, badge, className = '' }) {
  return (
    <button
      onClick={onClick}
      title={hint}
      aria-label={`${label} — ${hint}`}
      className={`relative flex flex-col items-center gap-1.5 px-1 py-3 text-center rounded-lg border border-ink/10 hover:bg-ink/[0.03] hover:border-primary/30 transition-colors ${className}`}
    >
      {badge && <span className="absolute top-1.5 left-1.5">{badge}</span>}
      <span
        className={`w-10 h-10 rounded-md flex items-center justify-center ${
          accent ? 'bg-primary/10 text-primary' : 'bg-ink/5 text-ink-muted'
        }`}
      >
        <Icon className="w-5 h-5" aria-hidden="true" />
      </span>
      <span className="text-xs font-medium text-ink leading-tight">{label}</span>
    </button>
  );
}

export const HomeHeroPlayer = ({ onNavigateTab, onOpenMajlisModal, onOpenMushaf, onOpenTools, onOpenFullscreen }) => {
  /*
    حال المجلس يُعرف قبل الدخول.

    كان الصفّ يقول «غرفة صوتية حيّة» دائماً، فيدخل المستخدم ليكتشف أنها
    مغلقة. والسؤال رخيص: طلب واحد يرد بحالة الغرفة وعدد الحاضرين.
  */
  const [majlis, setMajlis] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const read = async () => {
      const state = await fetchMajlisState(MAJLIS_ROOM_ID);
      if (!cancelled) setMajlis(state);
    };

    read();
    const timer = setInterval(read, MAJLIS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);
  const {
    currentStation,
    isPlaying,
    isLoading,
    togglePlay,
    playNextStation,
    playPrevStation,
    playStation,
    visibleStations,
    favorites,
    toggleFavorite,
    volume,
    setVolume,
    isMuted,
    toggleMute,
    siteSettings,
  } = useAudio();

  const station = currentStation || visibleStations?.[0];
  if (!station) return null;

  const isFav = favorites.includes(station.id);

  const show = Object.fromEntries(SERVICE_KEYS.map((k) => [k, isServiceVisible(siteSettings, k)]));
  // على التلفزيون لا يظهر إلا المصحف، فالعدّ للجوال؛ tv:grid-cols-1 يتولّى الباقي
  const visibleCount = SERVICE_KEYS.filter((k) => show[k]).length;
  const handlePlay = () => (currentStation ? togglePlay() : playStation(station));

  return (
    <div className="animate-fadeIn">
      {/* حقل الغلاف — يملأ العرض بلا إطار ولا استدارة. كان محبوساً في
          بطاقة مستديرة كبقية العناصر، فبدا المشغّل عنصراً في قائمة
          لا قلبَ التطبيق. */}
      <div className="relative -mx-4 -mt-4">
        {/* على التلفزيون شريطٌ قصير لا مربّع: بنسبة 5:4 بعرض الشاشة كان أطول من
            الشاشة نفسها، فيُدفع اسم الإذاعة وأزرار التشغيل تحتها خارج النظر */}
        <div className="relative aspect-[5/4] tablet:aspect-[16/9] tv:aspect-auto tv:h-36 overflow-hidden bg-surface-alt">
          {station.logo ? (
            <img src={station.logo} alt="" className="w-full h-full object-cover" />
          ) : (
            <StationArtwork station={station} className="w-full h-full" />
          )}

          {/* تدرّج يضمن قراءة النص فوق أي غلاف مهما كان لونه */}
          <div
            className="absolute inset-x-0 bottom-0 h-3/5 pointer-events-none"
            style={{
              background:
                'linear-gradient(to top, rgb(var(--c-paper)) 6%, rgb(var(--c-paper) / 0.92) 34%, transparent 100%)',
            }}
          />

          <span className="absolute top-3 right-4 flex items-center gap-1.5 text-2xs font-medium text-ink bg-paper/85 backdrop-blur-sm rounded-full px-2.5 py-1">
            <span className={`w-1.5 h-1.5 rounded-full bg-live ${isPlaying ? 'animate-breathe' : ''}`} />
            مباشر
          </span>

          <button
            onClick={() => toggleFavorite(station.id)}
            aria-label={isFav ? 'إزالة من المفضلة' : 'إضافة إلى المفضلة'}
            aria-pressed={isFav}
            className="absolute top-3 left-4 w-8 h-8 rounded-full bg-paper/85 backdrop-blur-sm flex items-center justify-center transition-transform active:scale-90"
          >
            <Heart
              className={`w-4 h-4 ${isFav ? 'fill-live text-live' : 'text-ink-muted'}`}
              aria-hidden="true"
            />
          </button>
        </div>

        <div className="relative -mt-20 px-4 pb-1">
          <h1 className="text-xl font-semibold text-ink leading-tight">{station.name}</h1>
          <p className="text-sm text-ink-muted mt-0.5">{station.reciter || 'بث مباشر'}</p>

          {/*
            الكتم يُقال صراحةً.

            كان التطبيق يبدو يشتغل تماماً — العدّاد يمشي وزرّ الإيقاف
            ظاهر — ولا صوت، فيظنّ المستخدم العطلَ في البثّ. والسبب
            مستوىً محفوظ من جلسة سابقة لا يراه أحد.
          */}
          {isPlaying && (isMuted || volume === 0) && (
            <button
              onClick={() => {
                if (isMuted) toggleMute();
                if (volume === 0) setVolume(0.85);
              }}
              className="mt-3 w-full text-right text-2xs text-live bg-live/10 border border-live/30 rounded-lg px-3 py-2 flex items-center gap-2"
            >
              <VolumeX className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
              <span>الصوت مكتوم — اضغط هنا لرفعه.</span>
            </button>
          )}

          <div className="flex items-center gap-1 mt-4">
            <button
              onClick={handlePlay}
              disabled={isLoading}
              aria-label={isPlaying ? 'إيقاف مؤقت' : 'تشغيل'}
              className="btn-primary w-14 h-14 rounded-full flex items-center justify-center shadow-glow disabled:opacity-60 flex-shrink-0"
            >
              {isLoading ? (
                <span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : isPlaying ? (
                <Pause className="w-6 h-6 fill-current" aria-hidden="true" />
              ) : (
                <Play className="w-6 h-6 fill-current -translate-x-0.5" aria-hidden="true" />
              )}
            </button>

            <button
              onClick={playPrevStation}
              aria-label="الإذاعة السابقة"
              className="w-11 h-11 rounded-full flex items-center justify-center text-ink-muted hover:text-ink transition-colors"
            >
              <SkipForward className="w-5 h-5" aria-hidden="true" />
            </button>
            <button
              onClick={playNextStation}
              aria-label="الإذاعة التالية"
              className="w-11 h-11 rounded-full flex items-center justify-center text-ink-muted hover:text-ink transition-colors"
            >
              <SkipBack className="w-5 h-5" aria-hidden="true" />
            </button>

            <button
              onClick={toggleMute}
              aria-label={isMuted || volume === 0 ? 'إلغاء الكتم' : 'كتم الصوت'}
              className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${
                isMuted || volume === 0 ? 'text-live' : 'text-ink-muted hover:text-ink'
              }`}
            >
              {isMuted || volume === 0 ? (
                <VolumeX className="w-[18px] h-[18px]" aria-hidden="true" />
              ) : (
                <Volume2 className="w-[18px] h-[18px]" aria-hidden="true" />
              )}
            </button>

            <button
              onClick={onOpenFullscreen}
              aria-label="شاشة التشغيل الكاملة"
              className="w-11 h-11 rounded-full flex items-center justify-center text-ink-muted hover:text-ink transition-colors"
            >
              <Maximize2 className="w-[18px] h-[18px]" aria-hidden="true" />
            </button>

            <button
              onClick={() => onNavigateTab('radios')}
              className="mr-auto text-xs text-primary font-medium flex items-center gap-0.5 hover:underline underline-offset-4"
            >
              كل الإذاعات
              <ChevronLeft className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <div className="-mx-4 mt-5 rule">
        <DayLine onOpen={() => onNavigateTab('prayer')} />
      </div>

      <YourDay onOpenMushaf={onOpenMushaf} onOpenTools={onOpenTools} />

      {/* الخانات بعدد الظاهر منها: إخفاءُ المسؤول خدمةً لا يترك فراغاً */}
      {visibleCount > 0 && (
        <div className={`mt-4 pb-10 grid ${GRID_COLS[visibleCount]} tv:grid-cols-1 gap-2`}>
          {show.mushaf && (
            <Entry
              icon={BookOpen}
              label="المصحف الشريف"
              hint="تلاوة بالرسم العثماني — تعمل بلا إنترنت"
              onClick={onOpenMushaf}
              accent
            />
          )}
          {show.majlis && (
            <Entry
              icon={Users}
              label="مجلس الطيبين"
              hint={
                majlis === null
                  ? 'غرفة صوتية حيّة للتدارس'
                  : majlis.isOpen
                    ? `مفتوح الآن — ${majlis.listeners} ${majlis.listeners === 1 ? 'حاضر' : 'حاضرون'}`
                    : 'مغلق الآن — لم يفتحه المذيع بعد'
              }
              badge={
                majlis?.isOpen ? (
                  <span className="block w-2 h-2 rounded-full bg-live animate-breathe" />
                ) : null
              }
              onClick={onOpenMajlisModal}
              accent={Boolean(majlis?.isOpen)}
              // على التلفزيون: لا ميكروفون غالباً، والكتابة بالريموت متعبة
              className="tv:hidden"
            />
          )}
          {show.fatwa && (
            <Entry
              icon={HelpCircle}
              label="الفتاوى"
              hint="أرسل سؤالك صوتاً أو كتابة"
              onClick={() => onNavigateTab('fatwa')}
              className="tv:hidden"
            />
          )}
          {show.studio && (
            <Entry
              icon={PhoneCall}
              label="استديو الإذاعة"
              hint="شارك على الهواء مباشرة"
              onClick={() => onNavigateTab('studio')}
              className="tv:hidden"
              accent={true}
              badge={
                <span className="block w-2 h-2 rounded-full bg-live animate-pulse" />
              }
            />
          )}
        </div>
      )}
    </div>
  );
};
